import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './IntegrationsModal.module.scss'
import { WordPressCard } from './components/IntegrationCards/WordPressCard'
import {
  ExternalIntegrationEnum,
  FacebookIntegration,
  GoHighLevelIntegration,
  GoogleIntegration,
  GoogleDriveIntegration,
  InstagramIntegration,
  LinkedInIntegration,
  MailchimpIntegration,
  ShopifyIntegration,
  TikTokIntegration,
  WixIntegration,
  WordPressIntegration,
  XIntegration,
  YouTubeIntegration,
} from 'api/clients/ExternalIntegrationsClient'
import { XCard } from './components/IntegrationCards/XCard'
import { LinkedinCard } from './components/IntegrationCards/LinkedinCard'
import { InstagramCard } from './components/IntegrationCards/InstagramCard'
import { FacebookCard } from './components/IntegrationCards/FacebookCard'
import { TikTokCard } from './components/IntegrationCards/TikTokCard'
import { YouTubeCard } from './components/IntegrationCards/YouTubeCard'
import { MailchimpCard } from './components/IntegrationCards/MailchimpCard'
import { GoHighLevelCard } from './components/IntegrationCards/GoHighLevelCard'
import { WixCard } from './components/IntegrationCards/WixCard'
import { ShopifyCard } from './components/IntegrationCards/ShopifyCard'
import { ZapierIntegration } from './components/ZapierIntegration/ZapierIntegration'
import { ApiKeySection } from './ApiKey/ApiKeySection'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { FunctionComponent } from 'react'
import { Heading } from 'blaze-ui/Heading/Heading'
import { partition } from 'lodash'
import { ExternalIntegrationCard } from './components/ExternalIntegrationCard/ExternalIntegrationCard'
import { ZapierLogo } from 'almanac-ui/icons/24'
import { Button } from 'blaze-ui/Button'
import { Close, GraduationCap } from 'blaze-ui/icons'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { HELP_CENTER_URL } from 'utils/constants'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { GoogleCard } from 'components/IntegrationsModal/components/IntegrationCards/GoogleCard'
import { GoogleDriveCard } from 'components/IntegrationsModal/components/IntegrationCards/GoogleDriveCard'
import { MetaAdsCard } from './components/IntegrationCards/MetaAdsCard'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'
import { IntegrationReauthBanner } from 'components/IntegrationReauthBanner/IntegrationReauthBanner'
import { useIntegrationHeartbeatStatus } from 'hooks/useIntegrationHeartbeatStatus'
import { useIsOnboardingPath } from 'hooks/useIsOnboardingPath'

type ComponentType =
  | FunctionComponent<{ integrations?: WordPressIntegration[]; canConnect?: boolean; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: LinkedInIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: FacebookIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: InstagramIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: XIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: TikTokIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: YouTubeIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: MailchimpIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: GoogleIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: GoogleDriveIntegration[]; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: GoHighLevelIntegration[]; canConnect?: boolean; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: WixIntegration[]; canConnect?: boolean; isDisconnected?: boolean }>
  | FunctionComponent<{ integrations?: ShopifyIntegration[]; canConnect?: boolean; isDisconnected?: boolean }>

const Providers: Record<ExternalIntegrationEnum, ComponentType> = {
  [ExternalIntegrationEnum.WordPress]: WordPressCard,
  [ExternalIntegrationEnum.X]: XCard,
  [ExternalIntegrationEnum.LinkedIn]: LinkedinCard,
  [ExternalIntegrationEnum.Instagram]: InstagramCard,
  [ExternalIntegrationEnum.Facebook]: FacebookCard,
  [ExternalIntegrationEnum.TikTok]: TikTokCard,
  [ExternalIntegrationEnum.YouTube]: YouTubeCard,
  [ExternalIntegrationEnum.Mailchimp]: MailchimpCard,
  [ExternalIntegrationEnum.Google]: GoogleCard,
  [ExternalIntegrationEnum.GoogleDrive]: GoogleDriveCard,
  [ExternalIntegrationEnum.MetaAds]: MetaAdsCard,
  [ExternalIntegrationEnum.GoHighLevel]: GoHighLevelCard,
  [ExternalIntegrationEnum.Wix]: WixCard,
  [ExternalIntegrationEnum.Shopify]: ShopifyCard,
}

export const IntegrationsModal = () => {
  const showApiCredentials = useFlipperFlag('api_keys')
  const { data } = useExternalIntegrations({ onError: () => showToastMessage('Unable to load integrations', 'error') })
  const { openModal, closeModal } = useModals()
  const tiktokEnabled = useFlipperFlag('tiktok_integration')
  const youtubeEnabled = useFlipperFlag('youtube_integration')
  const mailchimpEnabled = useFlipperFlag('mailchimp_integration')
  const googleEnabled = useFlipperFlag('google_integration')
  const openWindowSafely = useOpenWindowSafely()
  const { canConnectWordPress, canConnectGoHighLevel, canConnectWix, canConnectShopify } = useBlogIntegrations()
  const wixEnabled = useFlipperFlag('wix_blog_integration')
  const shopifyEnabled = useFlipperFlag('shopify_integration')
  const { disconnectedIntegrations } = useIntegrationHeartbeatStatus()
  const isOnboardingPath = useIsOnboardingPath()

  // Helper function to check if a specific provider has disconnected integrations
  const isProviderDisconnected = (provider: ExternalIntegrationEnum) => {
    return disconnectedIntegrations.some((integration) => integration.provider === provider)
  }

  const [connectedProviders, unconnectedProviders] = partition(
    Object.values(ExternalIntegrationEnum).filter((integration) => {
      if (integration === ExternalIntegrationEnum.YouTube) {
        return youtubeEnabled
      }
      if (integration === ExternalIntegrationEnum.TikTok) {
        return tiktokEnabled
      }
      if (integration === ExternalIntegrationEnum.Mailchimp) {
        return mailchimpEnabled
      }
      if (integration === ExternalIntegrationEnum.Google) {
        return googleEnabled
      }
      if (integration === ExternalIntegrationEnum.Wix) {
        return wixEnabled
      }
      if (integration === ExternalIntegrationEnum.Shopify) {
        return shopifyEnabled
      }
      return true
    }),
    (provider) => data?.external_integrations.some((integration) => integration.provider === provider),
  )

  const openHelpCenter = () => {
    openWindowSafely(`${HELP_CENTER_URL}/en/collections/9698779-integrations`)
  }

  if (isOnboardingPath) {
    closeModal()
    return null
  }

  return (
    <Modal.Root size="md" aria-label="Integrations">
      <Modal.Header
        title="Integrations"
        actions={
          <div className={Styles.actionsContainer}>
            <Button frontIcon={GraduationCap} variant="secondary" onPress={openHelpCenter}>
              Learn More about integrations
            </Button>
            <IconButton icon={Close} variant="tertiary" onPress={closeModal} />
          </div>
        }
      />
      <Modal.Content>
        <div className={Styles.content}>
          <IntegrationReauthBanner className={Styles.reauthBanner} hideButton={true} />
          {connectedProviders.length > 0 && (
            <div className={Styles.connections}>
              <Heading level={5}>Connected</Heading>
              <div className={Styles.cards}>
                {connectedProviders.map((provider) => {
                  const Component = Providers[provider]
                  const integrations = data?.external_integrations.filter(
                    (integration) => integration.provider === provider,
                  )
                  const providerIsDisconnected = isProviderDisconnected(provider)
                  // @ts-ignore
                  return (
                    <Component
                      key={provider}
                      integrations={integrations as any}
                      isDisconnected={providerIsDisconnected}
                    />
                  )
                })}
              </div>
            </div>
          )}
          {unconnectedProviders.length > 0 && (
            <div className={Styles.connections}>
              {connectedProviders.length > 0 && <Heading level={5}>Connect to some more services</Heading>}
              <div className={Styles.cards}>
                {unconnectedProviders.map((provider) => {
                  const Component = Providers[provider]
                  if (provider === ExternalIntegrationEnum.WordPress) {
                    return <Component key={provider} canConnect={canConnectWordPress} />
                  }
                  if (provider === ExternalIntegrationEnum.GoHighLevel) {
                    return <Component key={provider} canConnect={canConnectGoHighLevel} />
                  }
                  if (provider === ExternalIntegrationEnum.Wix) {
                    return <Component key={provider} canConnect={canConnectWix} />
                  }
                  if (provider === ExternalIntegrationEnum.Shopify) {
                    return <Component key={provider} canConnect={canConnectShopify} />
                  }
                  return <Component key={provider} />
                })}
              </div>
            </div>
          )}
          <div className={Styles.connections}>
            <Heading level={5}>Connect to Zapier</Heading>
            <ExternalIntegrationCard
              icon={<ZapierLogo />}
              title="Zapier"
              description="You can export to CMS, social media or ad managers"
              buttons={[
                <Button key="button" size="sm" variant="tertiary" onPress={() => openModal(ZapierIntegration)}>
                  Open
                </Button>,
              ]}
            />
          </div>
          {showApiCredentials && <ApiKeySection />}
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
